<template>
  <v-container>
    <v-snackbar v-model="snackbar" :color="snackColor" :timeout="3000" top>
      <v-row>
        <span>{{ snackText }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click.native="snackbar = false">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-row>
    </v-snackbar>

    <v-card>
      <v-card-title primary-title class="orange--text">
        <v-icon color="orange" class="mr-3">mdi-bell</v-icon>
        Notifications
        <v-chip v-if="unreadCount > 0" color="red" text-color="white" small class="ml-3">
          {{ unreadCount }} unread
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn
          v-if="unreadCount > 0"
          color="blue white--text"
          elevation="0"
          :loading="markingAll"
          @click="markAllAsRead"
        >
          <v-icon left>mdi-check-all</v-icon>
          Mark All as Read
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="notifications"
          :loading="loading"
          loading-text="Loading... Please wait"
          :items-per-page="15"
          dense
        >
          <!-- Status indicator -->
          <template v-slot:item.is_read="{ item }">
            <v-icon :color="item.is_read ? 'grey' : 'orange'">
              {{ item.is_read ? 'mdi-email-open' : 'mdi-email' }}
            </v-icon>
          </template>

          <!-- Title with unread highlight -->
          <template v-slot:item.title="{ item }">
            <span :class="item.is_read ? '' : 'font-weight-bold'">{{ item.title }}</span>
          </template>

          <!-- Date formatted -->
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn text small color="blue" @click="viewNotification(item)">
              <v-icon left small>mdi-eye</v-icon>
              View
            </v-btn>
            <v-btn
              v-if="!item.is_read"
              text
              small
              color="green"
              :loading="markingId === item.id"
              @click="markAsRead(item)"
            >
              <v-icon left small>mdi-check</v-icon>
              Mark Read
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- View Notification Dialog -->
    <v-dialog v-model="dialog" max-width="500">
      <v-card v-if="selected">
        <v-card-title class="orange--text">
          <v-icon color="orange" class="mr-2">mdi-bell</v-icon>
          {{ selected.title }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <p class="body-1">{{ selected.message }}</p>
          <v-divider class="my-3"></v-divider>
          <v-row dense>
            <v-col cols="6">
              <span class="caption grey--text">Type</span>
              <div>
                <v-chip small :color="typeColor(selected.type)" text-color="white">
                  {{ selected.type.replace('_', ' ') }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="6">
              <span class="caption grey--text">Booking ID</span>
              <div class="font-weight-bold">{{ selected.reference_id || '—' }}</div>
            </v-col>
            <v-col cols="6" class="mt-2">
              <span class="caption grey--text">Received</span>
              <div>{{ formatDate(selected.created_at) }}</div>
            </v-col>
            <v-col cols="6" class="mt-2">
              <span class="caption grey--text">Status</span>
              <div>
                <v-chip small :color="selected.is_read ? 'grey' : 'orange'" text-color="white">
                  {{ selected.is_read ? 'Read' : 'Unread' }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!selected.is_read"
            color="green white--text"
            elevation="0"
            :loading="markingId === selected.id"
            @click="markAsRead(selected)"
          >
            <v-icon left>mdi-check</v-icon>
            Mark as Read
          </v-btn>
          <v-btn text @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  adminNotifications,
  adminNotificationMarkRead,
  adminNotificationsMarkAllRead,
  headers
} from '../links'

export default {
  data () {
    return {
      notifications: [],
      loading: false,
      markingId: null,
      markingAll: false,
      dialog: false,
      selected: null,
      snackbar: false,
      snackColor: '',
      snackText: '',
      headers: [
        { text: '', value: 'is_read', width: '50px', sortable: false },
        { text: 'Title', value: 'title' },
        { text: 'Message', value: 'message' },
        { text: 'Date', value: 'created_at' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    unreadCount () {
      return this.notifications.filter(n => !n.is_read).length
    }
  },
  methods: {
    fetchNotifications () {
      this.loading = true
      this.$http.get(adminNotifications, { headers: headers(this.$cookies.get('userToken')) })
        .then(res => {
          this.notifications = res.body.notifications || []
        })
        .catch(err => {
          this.showSnack('error', err.body?.message || 'Failed to load notifications')
        })
        .finally(() => { this.loading = false })
    },
    viewNotification (item) {
      this.selected = item
      this.dialog = true
    },
    markAsRead (item) {
      this.markingId = item.id
      this.$http.post(adminNotificationMarkRead(item.id), {}, { headers: headers(this.$cookies.get('userToken')) })
        .then(() => {
          const n = this.notifications.find(n => n.id === item.id)
          if (n) {
            n.is_read = true
            n.read_at = new Date().toISOString()
          }
          if (this.selected && this.selected.id === item.id) {
            this.selected = { ...this.selected, is_read: true }
          }
          this.showSnack('success', 'Notification marked as read')
        })
        .catch(err => {
          this.showSnack('error', err.body?.message || 'Failed to mark as read')
        })
        .finally(() => { this.markingId = null })
    },
    markAllAsRead () {
      this.markingAll = true
      this.$http.post(adminNotificationsMarkAllRead, {}, { headers: headers(this.$cookies.get('userToken')) })
        .then(() => {
          this.notifications.forEach(n => { n.is_read = true })
          this.showSnack('success', 'All notifications marked as read')
        })
        .catch(err => {
          this.showSnack('error', err.body?.message || 'Failed to mark all as read')
        })
        .finally(() => { this.markingAll = false })
    },
    formatDate (val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('en-GB', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    },
    typeColor (type) {
      const map = {
        hotel_booking: 'blue',
        cruise_booking: 'teal',
        package_booking: 'purple',
        activity_booking: 'green'
      }
      return map[type] || 'grey'
    },
    showSnack (color, text) {
      this.snackColor = color
      this.snackText = text
      this.snackbar = true
    }
  },
  created () {
    this.fetchNotifications()
  }
}
</script>
